import { apiClient, type ApiResponse } from '@/api';
import { QR_API_URLS } from '@/api/constants/api.url';
import type {
    CreateQrInput,
    ListQrCodesParams,
    QrAnalytics,
    QrCode,
    QrDetailResponse,
    QrListResponse,
    QrPage,
    QrResponse,
    UpdateQrInput,
} from '@/modules/qr-codes/types';

export const qrCodesService = {
    /**
     * Fetch QR codes as a flat array. Used by drawers/maps that want the full
     * set (e.g. the properties table's QR-scan column) — pulls the 200-row max.
     */
    async list(params: ListQrCodesParams = {}): Promise<QrCode[]> {
        const { codes } = await this.listPaged({ limit: 200, ...params });
        return codes;
    },

    /** Fetch one page of QR codes plus the total match count + KPI stats. */
    async listPaged(params: ListQrCodesParams = {}): Promise<QrPage> {
        const { data } = await apiClient.get<ApiResponse<QrListResponse>>(QR_API_URLS.list, {
            params: {
                page:      params.page ?? 1,
                limit:     params.limit ?? 25,
                date_from: params.date_from,
                date_to:   params.date_to,
                q:         params.q,
                status:    params.status,
            },
        });
        return { codes: data.data.qr_codes, total: data.data.total, stats: data.data.stats };
    },
    async detail(id: string): Promise<QrCode> {
        const { data } = await apiClient.get<ApiResponse<QrDetailResponse>>(QR_API_URLS.detail(id));
        return data.data.qr_code;
    },
    async create(body: CreateQrInput): Promise<QrCode> {
        const { data } = await apiClient.post<ApiResponse<QrResponse>>(QR_API_URLS.create, body);
        return data.data.qr_code;
    },
    async update(id: string, body: UpdateQrInput): Promise<QrCode> {
        const { data } = await apiClient.patch<ApiResponse<QrResponse>>(QR_API_URLS.update(id), body);
        return data.data.qr_code;
    },
    async remove(id: string): Promise<void> {
        await apiClient.delete(QR_API_URLS.remove(id));
    },
    async analytics(id: string): Promise<QrAnalytics> {
        const { data } = await apiClient.get<ApiResponse<QrAnalytics>>(QR_API_URLS.analytics(id));
        return data.data;
    },
    /** Downloads the QR's scan history as a CSV blob (bearer-authed, so we fetch
     *  through the client rather than linking the URL directly). */
    async exportScans(id: string): Promise<Blob> {
        const { data } = await apiClient.get<Blob>(QR_API_URLS.scansCsv(id), { responseType: 'blob' });
        return data;
    },
};
